/**
 * The dark chart tooltip every chart the app draws on Insights shares (D67; S40 first, as S27 shipped
 * none): a dashed crosshair, then a dark panel with a heading ("Week of 3 Aug 2026") and one row per
 * line, its colour swatch, its name and its value, top down in the order the lines are listed.
 *
 * It looks like <x-insights.tooltip> (components/insights/tooltip.blade.php): gray-900 with gray-100
 * text, a 12px radius, 14px padding and the `float` shadow, 13px type, with tabular figures (P21). Those
 * hexes and the shadow are tailwind.config.js's, spelled here because echarts paints the panel itself.
 *
 * Imports nothing, so a Node harness can import it (InsightsChartTooltipTest), and so it never pulls
 * echarts into the bundle of a page that only formats with it.
 */

/** tailwind.config.js's gray-900, gray-100 and gray-400: the panel, its text and its muted heading. */
export const TOOLTIP_SURFACE = '#161e2e';
export const TOOLTIP_TEXT = '#f4f5f7';
export const TOOLTIP_MUTED = '#9fa6b2';

/** The crosshair: the spec's ink at a quarter, dashed 3 on 3. */
export const CROSSHAIR_COLOR = 'rgba(23, 37, 46, 0.25)';

/** Every name and value reaches the panel's markup through this: a topic's name is the site's own words. */
export const escapeHtml = (value) => String(value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]),
);

/**
 * The panel's markup for one point.
 *
 * @param {string} heading                                         Already worded by the server.
 * @param {Array<{name: string, color: string, value: string}>} rows Each value already formatted.
 */
export function renderInsightsTooltip(heading, rows) {
    const lines = rows.map(({ name, color, value }) => (
        '<div class="flex items-center justify-between gap-3">'
            + '<span class="flex min-w-0 items-center gap-1.5">'
                + `<span class="h-2 w-2 shrink-0 rounded-sm" style="background-color: ${escapeHtml(color)}"></span>`
                + `<span class="truncate">${escapeHtml(name)}</span>`
            + '</span>'
            + `<span class="shrink-0 tabular-nums">${escapeHtml(value)}</span>`
        + '</div>'
    )).join('');

    return `<div class="mb-1.5 text-gray-400">${escapeHtml(heading)}</div><div class="flex flex-col gap-1">${lines}</div>`;
}

/**
 * An axis-triggered echarts tooltip in this chrome.
 *
 * @param {(index: number) => string} heading               The heading of the point under the pointer.
 * @param {(index: number) => Array<{name, color, value}>} rows Its rows, every drawn line's, even one
 *        with no figure that week, so the panel's rows never jump between weeks.
 */
export function insightsAxisTooltip(heading, rows) {
    return {
        trigger: 'axis',
        axisPointer: {
            type: 'line',
            lineStyle: { color: CROSSHAIR_COLOR, width: 1, type: [3, 3] },
        },
        // Kept inside the chart's box: a chart on Insights sits in a card that clips its overflow.
        confine: true,
        backgroundColor: TOOLTIP_SURFACE,
        borderWidth: 0,
        padding: 14,
        textStyle: { color: TOOLTIP_TEXT, fontSize: 13 },
        extraCssText: 'width: 220px; max-width: 80vw; border-radius: 12px; box-shadow: 0 18px 40px -16px rgba(0, 0, 0, 0.5); line-height: 1.375;',
        formatter: (params) => {
            const index = Array.isArray(params) && params.length > 0 ? params[0].dataIndex : null;

            return index === null ? '' : renderInsightsTooltip(heading(index), rows(index));
        },
    };
}
